import { env } from '../config'

/**
 * Base API client for making HTTP requests
 * Wraps fetch with error handling and JSON parsing
 */

// API client object for convenient access
export const api = {
  get,
  post,
  patch,
  put,
  del,
}

export class ApiError extends Error {
  public status: number
  public statusText: string

  constructor(status: number, statusText: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
  }
}

export interface FetchOptions extends RequestInit {
  params?: Record<string, string>
}

/**
 * Make a GET request
 */
export async function get<T>(url: string, options?: FetchOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'GET' })
}

/**
 * Make a POST request
 */
export async function post<T>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
  return request<T>(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
}

/**
 * Make a PATCH request
 */
export async function patch<T>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
  return request<T>(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
}

/**
 * Make a PUT request
 */
export async function put<T>(url: string, data?: unknown, options?: FetchOptions): Promise<T> {
  return request<T>(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
}

/**
 * Make a DELETE request
 */
export async function del<T>(url: string, options?: FetchOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'DELETE' })
}

const API_BASE_URL = env.apiUrl
const AUTH_REFRESH_PATH = '/api/v1/auth/refresh'
const SKIP_REFRESH_PATHS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/logout',
  AUTH_REFRESH_PATH,
])

let refreshPromise: Promise<boolean> | null = null

function buildUrl(path: string, params?: Record<string, string>) {
  const base = API_BASE_URL ? API_BASE_URL.replace(/\/+$/, '') : ''
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = base ? `${base}${normalizedPath}` : normalizedPath
  return params ? `${url}?${new URLSearchParams(params)}` : url
}

function getPathname(url: string) {
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return new URL(url).pathname
    }
    if (typeof window !== 'undefined') {
      return new URL(url, window.location.origin).pathname
    }
    return url.split('?')[0] ?? url
  } catch {
    return url.split('?')[0] ?? url
  }
}

function shouldTryRefresh(url: string) {
  const pathname = getPathname(url)
  return !SKIP_REFRESH_PATHS.has(pathname)
}

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(buildUrl(AUTH_REFRESH_PATH), {
        method: 'POST',
        credentials: 'include',
      })
      return response.ok
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

async function parseResponse<T>(response: Response): Promise<T> {
  // Handle empty responses
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    return undefined as T
  }

  return response.json()
}

async function createApiError(response: Response) {
  const errorMessage = await response.text().catch(() => response.statusText)
  return new ApiError(response.status, response.statusText, errorMessage)
}

/**
 * Core request function with error handling
 */
async function request<T>(url: string, options?: FetchOptions): Promise<T> {
  const { params, ...fetchOptions } = options || {}

  // Build URL with query params
  const urlWithParams = buildUrl(url, params)

  const response = await fetch(urlWithParams, {
    credentials: 'include',
    ...fetchOptions,
  })

  if (response.status === 401 && shouldTryRefresh(urlWithParams)) {
    const refreshed = await refreshAccessToken()

    if (refreshed) {
      const retryResponse = await fetch(urlWithParams, {
        credentials: 'include',
        ...fetchOptions,
      })

      if (!retryResponse.ok) {
        throw await createApiError(retryResponse)
      }

      return parseResponse<T>(retryResponse)
    }
  }

  if (!response.ok) {
    throw await createApiError(response)
  }

  return parseResponse<T>(response)
}
